import { Schema } from "effect"
import { Rpc, RpcGroup } from "effect/rpc"
import { Command, EventRef } from "./commands/index"
import { Tier } from "./common"
import { WakeBatch, WakeReason } from "./domain/agentlife"
import { MemoryItem, MemoryScope, MemoryType } from "./domain/knowledge"
import { Agent } from "./domain/org"
import {
  AgentId,
  Epoch,
  IntegrationId,
  Markdown,
  NonNegInt,
  ProjectId,
  ProposalId,
  RunId,
  ShortRef,
  Slug,
  UserId,
  WorkerRef,
  WorkspaceId,
} from "./ids"
import { ContextPack, JobTemplate } from "./job"
import { Placement } from "./placement"
import { Run, RunKind, SessionRef, StopReason } from "./run"

// #region internal
/**
 * DO ↔ DO messages (§2.2). Facts travel through a transactional outbox: the row is written in the same transaction
 * as the event that caused it, an alarm drains it, and the receiver dedupes on `${log}:${seq}:${index}`
 * (inbox_dedupe). Delivery is at-least-once; every effect happens once. The caller's identity rides on the internal
 * call and becomes the actor; nothing in a payload can claim one. Reads across DOs are plain RPC, never outboxed.
 */
export const OutboxMeta = {
  /** The event that caused this message, in the sender's log. */
  source: EventRef,
  /** Position among the messages that event caused (one event can fan out). */
  index: NonNegInt,
} as const

/** Project → Workspace DO (the runner hub, workspace memory, the project index). */
export const WorkspaceInbound = Schema.TaggedUnion({
  /** Queue a runner run at the hub, with its frozen job template (C3: stored with the run, returned by claim). */
  Enqueue: {
    ...OutboxMeta,
    runId: RunId,
    projectId: ProjectId,
    kind: RunKind,
    ownerId: UserId,
    placement: Placement,
    job: JobTemplate,
    /** Display only (H8 `leases`), not part of the signed job (fix wave §4.4). */
    label: Schema.optionalKey(
      Schema.Struct({
        taskRef: Schema.NullOr(ShortRef),
        taskTitle: Schema.NullOr(Schema.String),
        /** Settings wave: the temporary teammate ("W-3"), shown as H8 `leases[].workerRef`. */
        workerRef: Schema.optionalKey(WorkerRef),
      }),
    ),
  },
  Cancel: { ...OutboxMeta, runId: RunId, reason: Schema.String },
  ApplyMemory: { ...OutboxMeta, item: MemoryItem }, // an approved workspace-scope memory proposal
  ProjectIndexChanged: {
    ...OutboxMeta,
    projectId: ProjectId,
    name: Schema.String,
    status: Schema.Literals(["active", "paused", "archived"]),
  },
})

/**
 * Into a Project DO: the hub's run facts (RecordRunWaiting / Leased / Requeued / Started / Milestone / Ended, actor
 * `runner` or `system:lease_reaper`), the Workspace's SyncWorkspace, an Agent DO's platform-turn facts and
 * OpenProposal{permission}, a Run DO's OpenProposal{permission} and stall signals. Decided like any command.
 */
export const ProjectInbound = Schema.Struct({ ...OutboxMeta, command: Schema.suspend(() => Command) })

/** hub → the run's stream sink (its Run DO, for every runner run, W2): refuse frames of this epoch from now on. */
export const RunSinkInbound = Schema.TaggedUnion({
  LeaseClosed: {
    ...OutboxMeta,
    runId: RunId,
    epoch: Epoch,
    reason: Schema.Literals(["final", "released", "expired", "revoked", "stale"]),
  },
})

/** permission.response to the run's sink, bound to the prompt it answers (inputHash). */
const PermissionDecision = {
  ...OutboxMeta,
  runId: RunId,
  requestId: Schema.String,
  inputHash: Schema.String,
  allow: Schema.Boolean,
  /** Settings wave §4.5: "this_turn" = allow this tool for the rest of the run (from approve{remember: "run"}). The
   *  Agent DO ignores it this wave. Absent = "once". */
  remember: Schema.optionalKey(Schema.Literals(["once", "this_turn"])),
} as const

/** Project → Agent DO (the body). PermissionDecision goes to the Run DO this wave (W2). */
export const AgentInbound = Schema.TaggedUnion({
  Wake: { ...OutboxMeta, agentId: AgentId, reason: WakeReason, key: Schema.String, cause: EventRef },
  PushAgentConfig: { ...OutboxMeta, agent: Agent }, // → AgentConfigSynced
  PermissionDecision,
  /** Every RunEnded of one of this agent's turn runs, whatever the reason (W17): the body never waits on a dead turn. */
  TurnRunEnded: { ...OutboxMeta, agentId: AgentId, runId: RunId, stopReason: StopReason },
})

/**
 * Project → Run DO. Prepare is sent for EVERY runner run (turns included, W2); for a turn `brief` is "" and the
 * body's prepareTurn adds the messages. The handoff (previous attempt's `next`, validator findings) is in the pack.
 */
export const RunInbound = Schema.TaggedUnion({
  Prepare: {
    ...OutboxMeta,
    run: Run,
    packRef: Schema.String,
    brief: Markdown,
    workspaceId: WorkspaceId,
    projectId: ProjectId,
    /** Limits and model for turn.start. */
    job: JobTemplate,
    /** Workers: the previous attempt's sessionOut; validators and turns: null. */
    sessionIn: Schema.NullOr(SessionRef),
  },
  PermissionDecision,
})

/** Project → User DO. */
export const UserInbound = Schema.TaggedUnion({
  Notify: {
    ...OutboxMeta,
    userId: UserId,
    projectId: ProjectId,
    proposalId: Schema.NullOr(ProposalId),
    tier: Tier,
  },
  /** A card resolved elsewhere closes itself (doneAt). */
  ResolveNotification: { ...OutboxMeta, userId: UserId, proposalId: ProposalId },
  ApplyMemory: { ...OutboxMeta, item: MemoryItem }, // an approved user-scope memory proposal
})

/** Platform-mind graph tools: the same tool layer the MCP endpoint serves (W7). */
export const GraphToolRequest = Schema.TaggedUnion({
  list: { runId: RunId },
  call: { runId: RunId, toolCallId: Schema.String, name: Schema.String, input: Schema.Unknown },
})
export const GraphToolDescriptor = Schema.Struct({
  name: Schema.String,
  description: Schema.String,
  inputSchema: Schema.JsonObject,
})
export const GraphToolResponse = Schema.TaggedUnion({
  list: { tools: Schema.Array(GraphToolDescriptor) },
  call: { isError: Schema.Boolean, output: Schema.Json },
})

/**
 * Agent DO → Project DO, RPC: start a turn run (the reply always carries the assembled pack this wave: runner-mind
 * turns need it too, W2), and the platform-mind graph tools.
 */
export class ProjectInternalRpcs extends RpcGroup.make(
  Rpc.make("RequestTurn", {
    payload: { runId: RunId, agentId: AgentId, wakeBatch: WakeBatch },
    success: Schema.Struct({ run: Run, pack: Schema.NullOr(ContextPack) }),
  }),
  Rpc.make("CallTool", { payload: GraphToolRequest, success: GraphToolResponse }),
) {}

/** Project → User / Workspace / Agent DO, RPC (read only): memory for context assembly. */
export const MemoryFilter = Schema.Struct({
  scope: MemoryScope,
  types: Schema.Array(MemoryType), // empty = all loadable types
  tags: Schema.Array(Slug),
  paths: Schema.Array(Schema.String),
  integrations: Schema.Array(IntegrationId),
})
export class MemoryStoreRpcs extends RpcGroup.make(
  Rpc.make("GetMemory", { payload: MemoryFilter, success: Schema.Array(MemoryItem) }),
) {}
// #endregion internal
